import { ScratchCardConfig } from './scratch-card.models';
import {mousePosition} from "./scratch-card.utils";

export class ScratchCard {
  canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private config: ScratchCardConfig;
  private isDragging = false;
  private callBackHandled = false;
  private maskReady = false;
  private static readonly FALLBACK_MASK_COLOR = '#C0C0C0';
  private ctx: CanvasRenderingContext2D;
  private element: HTMLElement;
  private eventListeners: { type: string; listener: EventListener }[] = [];

  constructor(cnf: ScratchCardConfig) {
    this.config = cnf;
    this.generateCanvas();
    this.context = this.canvas.getContext('2d') as CanvasRenderingContext2D;
    this.fillCanvas();
  }

  init(): void {
    this.addEventListeners();
  }

  private generateCanvas(): void {
    this.canvas = document.createElement('canvas');
    this.canvas.classList.add(this.config.canvasClass);
    this.canvas.width = this.config.width;
    this.canvas.height = this.config.height;
    this.config.element.appendChild(this.canvas);
  }

  private fillCanvas(): void {
    if (this.config.maskType === 'image') {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle CORS if needed
      img.onload = () => {
        try {
          this.context.drawImage(img, 0, 0, this.config.width, this.config.height);
        } catch {
          this.drawFallbackMask();
        }
        this.maskReady = true;
      };
      img.onerror = () => {
        // CORS failure or 404 — draw a solid fallback so the card isn't transparent
        this.drawFallbackMask();
        this.maskReady = true;
      };
      img.src = this.config.mask;
    } else {
      this.context.fillStyle = this.config.mask;
      this.context.fillRect(0, 0, this.config.width, this.config.height);
      this.maskReady = true;
    }
  }

  private drawFallbackMask(): void {
    this.context.fillStyle = ScratchCard.FALLBACK_MASK_COLOR;
    this.context.fillRect(0, 0, this.config.width, this.config.height);
  }

  private addEventListeners(): void {
    this.canvas.addEventListener('mousedown', (event) => this.handleDragStart(event));
    this.canvas.addEventListener('mousemove', (event) => this.handleDragMove(event));
    this.canvas.addEventListener('mouseup', () => this.handleDragEnd());
    this.canvas.addEventListener('mouseleave', () => this.handleDragEnd());

    let handleTouchStart = (event: TouchEvent) => {
      this.isDragging = true;
      const [x, y] = mousePosition(event, this.canvas);
      this.scratch(x, y);
      event.preventDefault();
    };

    let handleTouchMove = (event: TouchEvent) => {
      if (this.isDragging) {
        const [x, y] = mousePosition(event, this.canvas);
        this.scratch(x, y);
        event.preventDefault();
      }
    };

    let handleTouchEnd = () => {
      this.isDragging = false;
    };

    this.canvas.addEventListener('touchstart', handleTouchStart);
    this.canvas.addEventListener('touchmove', handleTouchMove);
    this.canvas.addEventListener('touchend', handleTouchEnd);
    this.canvas.addEventListener('touchcancel', handleTouchEnd);

    // Store for cleanup
    this.eventListeners.push(
      { type: 'touchstart', listener: handleTouchStart },
      { type: 'touchmove', listener: handleTouchMove },
      { type: 'touchend', listener: handleTouchEnd },
      { type: 'touchcancel', listener: handleTouchEnd }
    );
  }

  private handleDragStart(event: MouseEvent): void {
    this.isDragging = true;
    this.scratch(event.offsetX, event.offsetY);
  }

  private handleDragMove(event: MouseEvent): void {
    if (this.isDragging) {
      this.scratch(event.offsetX, event.offsetY);
    }
  }

  private handleDragEnd(): void {
    this.isDragging = false;
  }

  private scratch(x: number, y: number): void {
    if (!this.maskReady) return;
    this.context.globalCompositeOperation = 'destination-out';
    this.context.beginPath();
    this.context.arc(x, y, this.config.zoneRadius, 0, 2 * Math.PI);
    this.context.fill();

    const percent = this.updatePercent();
    if (percent >= this.config.percentToFinish && !this.callBackHandled) {
      this.config.callback();
      this.callBackHandled = true;
    }
  }

  private updatePercent(): number {
    const imageData = this.context.getImageData(0, 0, this.canvas.width, this.canvas.height);
    let counter = 0;

    for (let i = 0; i < imageData.data.length; i += 4) {
      if (imageData.data[i + 3] === 0) counter++;
    }

    return (counter / (this.canvas.width * this.canvas.height)) * 100;
  }
  private setupEventListeners(): void {
    const onScratch = (e: MouseEvent | TouchEvent) => {
      // Handle scratching logic
    };

    this.canvas.addEventListener('mousedown', onScratch);
    this.canvas.addEventListener('touchstart', onScratch);

    // Store listeners for cleanup
    this.eventListeners.push(
      { type: 'mousedown', listener: onScratch },
      { type: 'touchstart', listener: onScratch }
    );
  }

  private drawMask(): void {
    // Draw the mask on the canvas
    this.ctx.fillStyle = this.config.mask || '#FFCD00';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }
  destroy(): void {
    // Remove event listeners
    this.eventListeners.forEach(({ type, listener }) => {
      this.canvas.removeEventListener(type, listener);
    });

    // Remove the canvas from the DOM
    if (this.canvas.parentElement) {
      this.canvas.parentElement.removeChild(this.canvas);
    }
  }

}
